import monoWoff2 from '@vif/fonts/fonts/JetBrainsMono-Regular.subset.woff2?url';
import type { ReaderFont } from '../reader/orp-layout.js';

/**
 * Bundled reading fonts (PRD §8.2). JetBrains Mono is the default gated
 * experience and loads eagerly with font-display: block — a fallback-font
 * flash would move the ORP anchor, which the renderer must never allow.
 * The other faces lazy-load on first selection (H4): the @font-face is
 * injected and the file fetched only when a user picks the font.
 *
 * Family names: 'Vif Mono' aliases the JetBrains Mono subset (JBM has no
 * Reserved Font Name, aliasing is fine); 'Vif Neue' (from IBM Plex Mono,
 * RFN "Plex") and 'Vif Code' (from Source Code Pro, RFN 'Source') are the
 * REQUIRED renames per the fonts package license checklist.
 */

export type { ReaderFont };

export const READER_FONT_FAMILIES: Readonly<Record<ReaderFont, string>> = {
  mono: 'Vif Mono',
  plex: 'Vif Neue',
  code: 'Vif Code',
  dyslexic: 'Vif Dyslexic',
};

export const READER_FONT_STACKS: Readonly<Record<ReaderFont, string>> = {
  mono: "'Vif Mono', monospace",
  plex: "'Vif Neue', 'Vif Mono', monospace",
  code: "'Vif Code', 'Vif Mono', monospace",
  dyslexic: "'Vif Dyslexic', 'Vif Mono', monospace",
};

/** Picker order (H4): JetBrains Mono default, then Neue, Code, Dyslexic. */
export const READER_FONT_ORDER: readonly ReaderFont[] = ['mono', 'plex', 'code', 'dyslexic'];

const LAZY_FONT_FILES: Readonly<
  Record<Exclude<ReaderFont, 'mono'>, () => Promise<{ default: string }>>
> = {
  plex: () => import('@vif/fonts/fonts/VifNeue-Regular.subset.woff2?url'),
  code: () => import('@vif/fonts/fonts/VifCode-Regular.subset.woff2?url'),
  dyslexic: () => import('@vif/fonts/fonts/VifDyslexic-Regular.subset.woff2?url'),
};

function injectFace(family: string, url: string): void {
  const style = document.createElement('style');
  style.textContent = `
@font-face {
  font-family: '${family}';
  src: url('${url}') format('woff2');
  font-weight: 400;
  font-display: block;
}
`;
  document.head.appendChild(style);
}

export function installFonts(): void {
  injectFace(READER_FONT_FAMILIES.mono, monoWoff2);
}

const loaded = new Set<ReaderFont>(['mono']);

/** Idempotent lazy loader; resolves when the face is usable. */
export async function ensureFontLoaded(font: ReaderFont): Promise<void> {
  if (loaded.has(font)) return;
  loaded.add(font);
  try {
    const { default: url } = await LAZY_FONT_FILES[font as Exclude<ReaderFont, 'mono'>]();
    injectFace(READER_FONT_FAMILIES[font], url);
    await document.fonts.load(`16px '${READER_FONT_FAMILIES[font]}'`);
  } catch {
    loaded.delete(font); // allow a retry on transient failure
  }
}
